import apiClient from "./api-client.service";
import { Judge0Status, ProgrammingLanguage } from "../Interface/Index";

interface CompileCodeResponse {
  success: boolean;
  output: string;
  errorType?: string;
  errorTitle?: string;
  errorDescription?: string;
}

class CodeService {
  async getAllLanguages(): Promise<ProgrammingLanguage[]> {
    try {
      const response = await apiClient.get("/coding/languages");
      // Always return array, even if empty (backward compatible)
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error("CodeService -> getAllLanguages -> error", error);
      return []; // Return empty array on error - allows UI to work
    }
  }

  async getStatuses(): Promise<Judge0Status[]> {
    try {
      const response = await apiClient.get(`coding/statuses`);
      return response?.data || [];
    } catch (error: any) {
      console.error("CodeService -> getStatuses -> error", error);
      return [];
    }
  }

  async compileCode(
    sourceCode: string,
    languageId: number,
    useStructuredFormat: boolean = false
  ): Promise<string | null | CompileCodeResponse> {
    try {
      const response = await apiClient.post("/coding/compile", {
        sourceCode,
        languageId,
        ...(useStructuredFormat && { format: "structured" }), // Only send if flag is true
      });

      // Handle both formats for backward compatibility
      if (typeof response.data === "string") {
        // Old format: string
        return response.data;
      }

      if (response.data === null) {
        // Old format: null on error
        return null;
      }

      // New format: structured object
      if (useStructuredFormat && typeof response.data === "object") {
        return {
          success: response.data.success ?? false,
          output: response.data.output || "",
          errorType: response.data.errorType,
          errorTitle: response.data.errorTitle,
          errorDescription: response.data.errorDescription,
        };
      }

      // Fallback: try to extract string from object (for backward compatibility)
      if (typeof response.data === "object" && response.data.output) {
        return response.data.output;
      }

      return response.data || null;
    } catch (error: any) {
      console.error("CodeService -> compileCode -> error", error);

      // Return appropriate format based on flag
      if (useStructuredFormat) {
        return {
          success: false,
          output:
            "Unable to test code at this time. You can still submit your code and it will be considered for evaluation.",
          errorType: "SERVICE_ERROR",
          errorTitle: "Service Unavailable",
          errorDescription:
            "Unable to test code at this time. You can still submit your code and it will be considered for evaluation.",
        };
      }

      return null; // Old format
    }
  }
}

const codeService = new CodeService();
export default codeService;
